import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { useDarkMode } from "../hooks/useDarkMode";
import { FaVideo, FaStethoscope, FaInfoCircle } from "react-icons/fa";
import { Layers } from "lucide-react";

import { useTranslation } from "react-i18next";
import { getDepartments, getDoctors, getDepartmentAvailableSlots, getDoctorFees, checkDoctorWalkins, createSlotsWebSocket } from "../Services/api";
import AvailableSlotsPopup from "./AvailableSlotsPopup";
import { toast } from "react-toastify";
import Select from "react-select";
import Loader from './Loader';
import TelemedicineInfoPopup from "./TelemedicineInfoPopup";

const reactSelectStyles = (isDark, isReadOnly) => ({
  control: (base, state) => ({
    ...base,
    backgroundColor: isReadOnly
      ? isDark
        ? "#374151" // match dark:bg-gray-700 for readonly
        : "#f9fafb" // match bg-gray-50 for readonly
      : isDark
        ? "#1f2937" // normal dark mode editable
        : "#ffffff", // normal light mode editable
    cursor: isReadOnly ? "not-allowed" : "pointer",
    borderColor: state.isFocused
      ? "#3b82f6"
      : isDark
        ? "#4b5563"
        : "#d1d5db",
    boxShadow: state.isFocused ? "0 0 0 1px #3b82f6" : "none",
  }),

  menu: (base) => ({
    ...base,
    backgroundColor: isDark ? "#1f2937" : "#ffffff",
    zIndex: 9999,
  }),

  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),

  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused
      ? isDark
        ? "#374151"
        : "#e5e7eb"
      : state.isSelected
        ? "#2563eb"
        : isDark
          ? "#1f2937"
          : "#ffffff",
    color: state.isSelected ? "#ffffff" : isDark ? "#f9fafb" : "#111827",
    cursor: "pointer",
  }),

  singleValue: (base) => ({
    ...base,
    color: isReadOnly
      ? isDark
        ? "#9ca3af" // dimmed text for readonly dark
        : "#6b7280" // dimmed text for readonly light
      : isDark
        ? "#f9fafb"
        : "#111827",
  }),

  input: (base) => ({
    ...base,
    color: isReadOnly
      ? isDark
        ? "#9ca3af"
        : "#6b7280"
      : isDark
        ? "#f9fafb"
        : "#111827",
  }),

  placeholder: (base) => ({
    ...base,
    color: isDark ? "#9ca3af" : "#6b7280",
  }),
});



export default function DoctorDetails({ form, onChange, isExistingPatient }) {
  const { t } = useTranslation();
  const isDark = useDarkMode();
  const wsRef = useRef(null);
  const [showTelemedicine, setShowTelemedicine] = useState(false);
  const [showTeleInfo, setShowTeleInfo] = useState(false);
  const [visitType, setVisitType] = useState("W"); // "W" = Walk-in, "A" = Appointment

  const isAppointmentEnabled = process.env.REACT_APP_IS_APPOINTMENT_ENABLED === "true";
  const isWalkinEnabled = process.env.REACT_APP_IS_WALKIN_ENABLED === "true";

  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(true);
  const [deptError, setDeptError] = useState(null);

  const [doctors, setDoctors] = useState([]);

  const [doctorError, setDoctorError] = useState(null);
  const [showNotes, setShowNotes] = useState(false);
  const [selectedDoctorId, setSelectedDoctorId] = useState(false);
  const [selectedSpeciality, setSelectedSpeciality] = useState(null);
  const [slotConflictError, setSlotConflictError] = useState(null);

  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [appointmentDate, setAppointmentDate] = useState("");
  const [openSlots, setOpenSlots] = useState(false);
  const fee_type = isExistingPatient ? "genreg" : "genreg";
  const [doctorFee, setDoctorFee] = useState(null);
  const payGatewayEnabled = process.env.REACT_APP_PAY_GATEWAY_ENABLE === "true";
  const payMandatory = process.env.REACT_APP_PAY_MANDATORY === "true";
  const payAtCounterEnabled = process.env.REACT_APP_PAY_AT_COUNTER_ENABLED === "true";
  const showPayNow = payGatewayEnabled;
  const showPayCounter = payAtCounterEnabled;
  const disablePayCounter = payMandatory;
  const forcePayNow = payMandatory && payGatewayEnabled;
  const invalidConfig = payMandatory && !payGatewayEnabled;
  const onlyPayCounterAvailable = showPayCounter && !showPayNow;
  const [isFreeConsultation, setIsFreeConsultation] = useState(false);
  const handleSlotSelect = (slot) => {
    if (!slot?.available) {
      toast.error(t("slotErrors.noLongerAvailable"));
      return;
    }

    onChange({
      target: { name: "slotDateTime", value: slot }
    });
    setSlotConflictError(null);

    setOpenSlots(false); // optionally close popup after selection
  };
  const VISIT_TYPE = {
    WALK_IN: "W",
    APPOINTMENT: "A"
  };
  const handleWalkIn = async () => {
    try {
      setLoadingMessage("Fetching Doctors...");
      setLoading(true);
      setVisitType(VISIT_TYPE.WALK_IN);

      await getDoctors(
        VISIT_TYPE.WALK_IN,
        null,
        form.appointmentDate
      );

    } catch (err) {
      console.error("Error fetching doctors:", err.response || err);
      setDoctorError("Failed to load doctors");
      setDoctors([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAppointment = async () => {
    try {
      setLoadingMessage("Fetching Doctors...");
      setLoading(true);
      setVisitType(VISIT_TYPE.APPOINTMENT);

      await getDoctors(
        VISIT_TYPE.APPOINTMENT,
        null,
        form.appointmentDate
      );

    } catch (err) {
      console.error("Error fetching doctors:", err.response || err);
      setDoctorError("Failed to load doctors");
      setDoctors([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = async (e) => {
    const selectedDate = e.target.value;

    setAppointmentDate(selectedDate);
    onChange({
      target: { name: "appointmentDate", value: selectedDate }
    });

    // Only refetch for Appointment
    if (form.visitType === "appointment") {
      try {
        setLoadingMessage("Fetching Doctors...");
        setLoading(true);

        const res = await getDoctors(
          VISIT_TYPE.APPOINTMENT,
          form.selectedDepartment ? form.selectedDepartment : null, // pass null if not selected
          selectedDate
        );
         setDoctors((res?.data || []).map((doc) => ({
          doctorId: String(doc.doctorId || doc.id),
          name: doc.name,
          specialities: doc.specialities || [],
        })));
        setDoctorError(null);
      } catch (err) {
        console.error("Error fetching doctors:", err.response || err);
        setDoctorError("Failed to load doctors");
        setDoctors([]);
      } finally {
        setLoading(false);
      }
    }
  };


  useEffect(() => {
    if (!selectedDoctorId || !appointmentDate) return;

    if (!wsRef.current) {
      wsRef.current = createSlotsWebSocket((data) => {
        console.log("📩 WS update:", data);
        const myPatientId = localStorage.getItem("patientId");


        // Always update UI
        setSlots((prev) =>
          prev.map((slot) =>
            slot.slotId === data.slotId ? { ...slot, available: false } : slot
          )
        );

        // Ignore own booking
        if (String(data.patientId) === String(myPatientId)) {
          console.log("🟢 Own booking event — ignoring");
          return;
        }

        // Only others reach here
        if (form.slotDateTime?.slotId === data.slotId) {
          setSlotConflictError(
            t("slotErrors.justBooked")
          );
          onChange({ target: { name: "slotDateTime", value: null } });
        }
      });

    }

    return () => {
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [selectedDoctorId, appointmentDate, form.slotDateTime]);


  useEffect(() => {
    setIsFreeConsultation(doctorFee === 0);
  }, [doctorFee]);
  useEffect(() => {
    if (invalidConfig) {
      console.warn("Payment mandatory but gateway disabled. Check env config.");
    }
  }, [invalidConfig]);

  const [paymentMode, setPaymentMode] = useState("pay_now");

  const todayDate = new Date();
  const day = String(todayDate.getDate()).padStart(2, '0');
  const month = String(todayDate.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
  const year = todayDate.getFullYear();
  const today = `${year}-${month}-${day}`;

  const [anyDepartment, setAnyDepartment] = useState(false);
  const [isWalkinsAllowed, setiIWalkinsAllowed] = useState(false);
  const handleChange = (mode) => {
    onChange({ target: { name: "paymentMode", value: mode } });
  };
  //   useEffect(() => {
  //     if (forcePayNow && form.paymentMode !== "pay_now") {
  //       handleChange("pay_now");
  //     }
  //   }, [forcePayNow, form.paymentMode]);
  //   useEffect(() => {
  //     if (onlyPayCounterAvailable && form.paymentMode !== "pay_counter") {
  //       handleChange("pay_counter");
  //     }
  //   }, [onlyPayCounterAvailable, form.paymentMode]);
  //  useEffect(() => {
  //     if (doctorFee === 0) {
  //       if (form.paymentMode !== null) {
  //         onChange({ target: { name: "paymentMode", value: null } });
  //       }
  //     }
  //   }, [doctorFee, form.paymentMode]);
  //   useEffect(() => {
  //     if (doctorFee > 0 && !form.paymentMode) {
  //       if (forcePayNow) {
  //         onChange({ target: { name: "paymentMode", value: "pay_now" } });
  //       } else if (onlyPayCounterAvailable) {
  //         onChange({ target: { name: "paymentMode", value: "pay_counter" } });
  //       }
  //     }
  //   }, [doctorFee, form.paymentMode, forcePayNow, onlyPayCounterAvailable]);

  useEffect(() => {
    if (form.visitType === "appointment" && !form.appointmentType) {
      onChange({
        target: { name: "appointmentType", value: "in_person" }
      });
    }
  }, [form.visitType]);


  useEffect(() => {
    if (form.specialNotes && form.specialNotes.trim() !== "") {
      setShowNotes(true);
    }
  }, [form.specialNotes]);
  useEffect(() => {
    let nextMode = form.paymentMode;
    console.log("FEE", doctorFee);

    // Free consultation → no payment
    if (doctorFee === 0) {
      nextMode = null;
    } else {
      // Paid consultation → apply rules
      if (forcePayNow) nextMode = "pay_now";
      else if (onlyPayCounterAvailable) nextMode = "pay_counter";
    }

    if (nextMode !== form.paymentMode) {
      onChange({ target: { name: "paymentMode", value: nextMode } });
    }

  }, [doctorFee, forcePayNow, onlyPayCounterAvailable, form.paymentMode]);

  useEffect(() => {
    if (isWalkinEnabled === undefined || isAppointmentEnabled === undefined) return;
    if (isWalkinEnabled) {
      onChange({ target: { name: "visitType", value: "walkin" } });

      setAppointmentDate(today);
      onChange({ target: { name: "appointmentDate", value: today } });
      onChange({ target: { name: "slotDateTime", value: null } });

    } else if (isAppointmentEnabled) {
      onChange({ target: { name: "visitType", value: "appointment" } });

      setAppointmentDate(today);
      onChange({ target: { name: "appointmentDate", value: today } });

    } else {
      onChange({ target: { name: "visitType", value: "" } });
    }
  }, [isWalkinEnabled, isAppointmentEnabled]);




  useEffect(() => {
    if (!form.paymentMode) {
      onChange({ target: { name: "paymentMode", value: "pay_now" } });
    }
  }, []);
  useEffect(() => {
    if (form.consultationFee == null) {
      setDoctorFee(null);
    }
  }, [form.consultationFee]);



  useEffect(() => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const year = today.getFullYear();
    const formattedDate = `${year}-${month}-${day}`;
    setAppointmentDate(formattedDate);
  }, []);



  // Fetch departments
  useEffect(() => {
    const fetchDepartments = async () => {
      setLoadingDepartments(true);
      try {
        const res = await getDepartments();
        setDepartments(res?.data || []);
        setDeptError(null);
      } catch (err) {
        console.error("Error fetching departments:", err.response || err);
        setDeptError("Failed to load departments");
      } finally {
        setLoadingDepartments(false);
      }
    };
    fetchDepartments();
  }, []);

  // Fetch doctors when department changes
  useEffect(() => {
    if (anyDepartment) return; // skip if checkbox is ON
    if (!form.selectedDepartment) return;
    setLoadingMessage("Fetching Doctors...");
    setLoading(true);

    const fetchDoctors = async () => {
      try {
        let type = VISIT_TYPE.WALK_IN;

        if (form.visitType === "appointment") {
          type = VISIT_TYPE.APPOINTMENT;
        }
        const res = await getDoctors(type, form.selectedDepartment, form.appointmentDate);
        setDoctors((res?.data || []).map((doc) => ({
          doctorId: String(doc.doctorId || doc.id),
          name: doc.name,
          specialities: doc.specialities || [],
        })));
        setDoctorError(null);
      } catch (err) {
        console.error("Error fetching doctors:", err.response || err);
        setDoctorError("Failed to load doctors");
        setDoctors([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDoctors();
  }, [form.selectedDepartment]);

  const handleFetchSlots = async () => {
    if (!form.selectedDoctor) {
      toast.warn("Please select a doctor first");
      return;
    }
    try {
      setLoadingSlots(true);
      const allSlots = await getDepartmentAvailableSlots(form.selectedDoctor, appointmentDate, fee_type);
      const doctorObj = allSlots.find(doc => String(doc.doctorId) === String(form.selectedDoctor));
      setSlots(doctorObj?.availableSlots || []);
    } catch (err) {
      console.error("Error fetching slots:", err);
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };
  const [formData, setFormData] = useState({
    visitType: null,
    selectedDoctor: null,
    slotDateTime: null,
    paymentMode: null
  });
  const stepConfig = {
    walkin: [
      t("doctor.steps.visit"),
      t("doctor.steps.doctor"),
      t("doctor.steps.payment"),
      t("doctor.steps.confirm")
    ],
    appointment: [
      t("doctor.steps.visit"),
      t("doctor.steps.doctor"),
      t("doctor.steps.slot"),
      t("doctor.steps.payment"),
      t("doctor.steps.confirm")
    ]
  };

  const steps = stepConfig[form.visitType] || [];



  const getCurrentStep = (form) => {
    if (!form.visitType) return 0;              // Visit
    if (!form.selectedDoctor) return 1;         // Doctor

    if (form.visitType === "appointment") {
      if (!form.slotDateTime) return 2;         // Slot
      if (!form.paymentMode) return 3;          // Payment
      return 4;                                 // Confirm
    } else {
      if (!form.paymentMode) return 2;          // Payment
      return 3;                                 // Confirm
    }
  };

  const currentStep = getCurrentStep(form);

  useEffect(() => {
    setFormData(f => ({ ...f, slotDateTime: null }));
  }, [form.selectedDoctor]);

  useEffect(() => {
    setFormData(f => ({ ...f, slotDateTime: null, paymentMode: null }));
  }, [form.visitType]);
  // derive and apply paymentMode deterministically
  useEffect(() => {
    // canonical inputs
    const doctorSelected = !!form.selectedDoctor;
    const fee = doctorFee; // can be null, 0, or >0

    // Treat doctorFee === 0 as "free consultation"
    const isFree = fee === 0;

    // Compute desired paymentMode from scratch (single source of truth)
    let desired = null;

    if (isFree) {
      // free -> disable payments
      desired = null;
    } else if (!doctorSelected) {
      // doctor cleared -> restore initial / default rules (like on load)
      // Priority: forced online -> only-counter -> prefer online if available -> prefer counter if available -> null
      if (forcePayNow) desired = "pay_now";
      else if (onlyPayCounterAvailable) desired = "pay_counter";
      else if (showPayNow) desired = "pay_now";
      else if (showPayCounter) desired = "pay_counter";
      else desired = null;
    } else {
      // doctor selected and not free (fee > 0)
      if (forcePayNow) desired = "pay_now";
      else if (onlyPayCounterAvailable) desired = "pay_counter";
      else {
        // preserve existing selection if user already picked one, otherwise prefer pay_now if available
        desired = form.paymentMode || (showPayNow ? "pay_now" : showPayCounter ? "pay_counter" : null);
      }
    }

    // update isFreeConsultation state (if you keep it)
    setIsFreeConsultation(isFree);

    // only call parent onChange when actual value differs (avoid loops)
    if (desired !== form.paymentMode) {
      onChange({ target: { name: "paymentMode", value: desired } });
    }
  }, [
    form.selectedDoctor,
    doctorFee,
    forcePayNow,
    onlyPayCounterAvailable,
    showPayNow,
    showPayCounter,
    // don't include form.paymentMode in deps to avoid timing confusion; we compare before calling onChange
  ]);



  const handleDoctorChange = async (e) => {
    console.log("SEL DOC", form.selectedDoctor);

    const selectedDoctorId = e.target.value;
    setSelectedDoctorId(selectedDoctorId);
    setDoctorFee(null);
    setSelectedSpeciality(null);
    onChange({ target: { name: "selectedSpeciality", value: null } });
    onChange({ target: { name: "slotDateTime", value: null } });
    onChange({ target: { name: "doctorFee", value: "" } });
    onChange({ target: { name: "consultationFee", value: "" } });
    const selectedDoctor = doctors.find(doc => doc.doctorId === selectedDoctorId);

    // update doctor selection
    onChange(e);

    if (!selectedDoctorId) return;

    // Auto-select speciality if only one option
    if (selectedDoctor?.specialities?.length === 1) {
      const s = selectedDoctor.specialities[0];
      const autoSelected = typeof s === "string"
        ? { value: s, label: s }
        : { value: s.id ?? s.code ?? s.name, label: s.name };
      setSelectedSpeciality(autoSelected);
      onChange({ target: { name: "selectedSpeciality", value: autoSelected.value } });
    }


    // set doctor name
    onChange({
      target: {
        name: "selectedDoctorName",
        value: selectedDoctor ? selectedDoctor.name : ""
      }
    });

    // 🔥 fetch doctor fee
    try {
      setLoadingMessage("Fetching Doctor Fees...");
      setLoading(true);
      const rawPatientId = localStorage.getItem("patientId");

      const patientIdInt =
        rawPatientId && rawPatientId !== "null" && rawPatientId !== "undefined"
          ? rawPatientId
          : "";

      console.log("ID", patientIdInt);


      const feeResponse = await getDoctorFees(selectedDoctorId, fee_type, patientIdInt);

      if (feeResponse) {
        setDoctorFee(feeResponse.consultationFee);
        setShowTelemedicine(feeResponse.telemedicineEnabled);
        // also store inside form if needed
        onChange({
          target: {
            name: "consultationFee",
            value: feeResponse.consultationFee
          }
        });
        if (form.visitType === "appointment" && selectedDoctorId) {
          setLoadingMessage("Fetching Available Slots...");
          const allSlots = await getDepartmentAvailableSlots(selectedDoctorId, appointmentDate, fee_type);
          const doctorObj = allSlots.find(doc => String(doc.doctorId) === String(selectedDoctorId));
          setSlots(doctorObj?.availableSlots || []);
          setOpenSlots(true); // open popup
        }
      }

    } catch (err) {
      console.error("Error fetching doctor fee", err);
      setDoctorFee(null);
      setSlots([]);
    } finally {
      setLoading(false);
    }


    // ************************************
    // 🔥 2. Call checkDoctorWalkins API
    // ************************************

    // ************************************
    // 🔥 2. Call checkDoctorWalkins API (WALK-IN ONLY)
    // ************************************
    if (form.visitType === "walkin") {
      try {
        setLoadingMessage("Checking Walk-in Availability...");
        setLoading(true);

        const walkinsResponse = await checkDoctorWalkins(
          selectedDoctorId,
          appointmentDate // YYYY-MM-DD
        );

        if (walkinsResponse) {
          const available = walkinsResponse.availableWalkins;

          // update form state
          onChange({
            target: {
              name: "availableWalkins",
              value: available
            }
          });

          // ❌ If no slots available → show error + disable submit
          if (available <= 0) {
            toast.error("No walk-in slots available for this doctor on the selected date.");
            setiIWalkinsAllowed(false);
          }
          else {
            // enable submit button
            setiIWalkinsAllowed(true);
          }
        }
      } catch (err) {
        console.error("Error checking walk-ins", err);
        toast.error("Failed to check walk-in availability.");
      } finally {
        setLoading(false);
      }
    }
  }
  useEffect(() => {
    if (form.visitType === "walkin" && form.appointmentType !== "in_person") {
      onChange({
        target: { name: "appointmentType", value: "in_person" }
      });
    }
  }, [form.visitType]);


  return (
    <div className="relative">
      {/* Show loader on top of the form */}
      {loading && <Loader message={loadingMessage} />}
      <div className="rounded-2xl p-5 mb-2 transition-all duration-300 glass-card"
           style={{
             borderLeft: "4px solid #2563eb",
           }}>
        <h3 className="font-extrabold text-base sm:text-lg mb-4 flex items-center gap-2"
            style={{ color: "var(--text-primary)", letterSpacing: "-0.01em" }}>
          <span
            className="inline-flex items-center justify-center w-7 h-7 rounded-xl flex-shrink-0"
            style={{ background: "linear-gradient(135deg, #2563eb, #0891b2)" }}
          >
            <FaStethoscope className="text-white" style={{ fontSize: "13px" }} />
          </span>
          {t("doctor.title")}
        </h3>
        {steps.length > 0 && (
          <div className="flex items-center mb-6 w-full overflow-x-auto pb-1">
            {steps.map((step, idx) => {
              const isActive = idx === currentStep;
              const isCompleted = idx < currentStep;
              return (
                <div key={idx} className="flex items-center flex-shrink-0">
                  <div className="flex flex-col items-center gap-1">
                    <div
                      className={`flex items-center justify-center rounded-full font-extrabold text-[11px] transition-all duration-300${isActive ? " step-active-pulse" : ""}`}
                      style={{
                        width: 30, height: 30,
                        background: isCompleted
                          ? "linear-gradient(135deg,#10b981,#059669)"
                          : isActive
                            ? "linear-gradient(135deg,#2563eb,#0891b2)"
                            : isDark ? "#334155" : "#f3f4f6",
                        color: isCompleted || isActive ? "#fff" : (isDark ? "#64748b" : "#9ca3af"),
                        border: isCompleted || isActive ? "none" : `1.5px solid ${isDark ? "#475569" : "#e5e7eb"}`,
                      }}
                    >
                      {isCompleted ? "✓" : idx + 1}
                    </div>
                    <span
                      className="text-[9px] sm:text-[10px] text-center leading-snug font-semibold"
                      style={{
                        color: isCompleted ? "#10b981" : isActive ? "#60a5fa" : (isDark ? "#64748b" : "#9ca3af"),
                        maxWidth: 64,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                        display: "block",
                      }}
                    >
                      {step}
                    </span>
                  </div>
                  {idx < steps.length - 1 && (
                    <div
                      className="mx-1.5 sm:mx-2 transition-all duration-300 self-start mt-3.5"
                      style={{
                        width: 20, height: 2,
                        borderRadius: 2,
                        background: isCompleted
                          ? "linear-gradient(90deg,#10b981,#059669)"
                          : isDark ? "#475569" : "#e5e7eb",
                        flexShrink: 0,
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}




        {/* ============================
    VISIT TYPE (iOS Style Buttons)
=============================== */}
        <div className="mt-5 w-full md:w-1/2">
          <label className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2 block">
            {t("visit.visitType") || "Visit Type"}
          </label>
          <div className="relative flex rounded-2xl p-1"
               style={{ background: "var(--surface-hover)" }}>
            {/* Sliding indicator */}
            {isWalkinEnabled && isAppointmentEnabled && (
              <div
                className="absolute top-1 h-[calc(100%-0.5rem)] w-1/2 rounded-xl shadow-md transition-all duration-300"
                style={{
                  background: "linear-gradient(135deg,#2563eb,#0891b2)",
                  left: form.visitType === "appointment" ? "50%" : "4px",
                }}
              />
            )}
            {isWalkinEnabled && (
              <button
                type="button"
                className={`relative flex-1 px-3 py-2.5 rounded-xl text-center text-xs sm:text-sm font-bold leading-tight transition-all duration-200 ${form.visitType === "walkin" ? "text-white" : "text-gray-500"}`}
                style={{ touchAction: "manipulation", minHeight: 44 }}
                onClick={() => {
                  onChange({ target: { name: "visitType", value: "walkin" } });
                  setAppointmentDate(today);
                  onChange({ target: { name: "appointmentDate", value: today } });
                  onChange({ target: { name: "slotDateTime", value: null } });
                  handleWalkIn();
                }}
              >
                🚶 {t("visit.walkin")}
              </button>
            )}
            {isAppointmentEnabled && (
              <button
                type="button"
                className={`relative flex-1 px-3 py-2.5 rounded-xl text-center text-xs sm:text-sm font-bold leading-tight transition-all duration-200 ${form.visitType === "appointment" ? "text-white" : "text-gray-500"}`}
                style={{ touchAction: "manipulation", minHeight: 44 }}
                onClick={() => {
                  onChange({ target: { name: "visitType", value: "appointment" } });
                  setAppointmentDate(today);
                  onChange({ target: { name: "appointmentDate", value: today } });
                  handleAppointment();
                }}
              >
                📅 {t("visit.appointment")}
              </button>
            )}
          </div>
        </div>


        {/* ============================
    APPOINTMENT DATE
=============================== */}
        <div className="mt-5 w-full md:w-1/2">
          <label className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2 block">
            {t("visit.selectDate")} <span className="text-red-500">*</span>
          </label>
          <div
            className="flex items-center gap-2 px-3 py-2.5 rounded-xl transition-all"
            style={{
              background: form.visitType === "walkin" ? "var(--surface-secondary)" : "var(--surface)",
              border: "1.5px solid " + (form.visitType === "walkin" ? "var(--border)" : "var(--border-accent)"),
              boxShadow: form.visitType === "walkin" ? "none" : "0 1px 4px rgba(37,99,235,0.08)",
            }}
          >
            <span style={{ fontSize: 16 }}>📅</span>
            <input
              type="date"
              min={today}
              disabled={form.visitType === "walkin"}
              className={`flex-1 bg-transparent text-sm font-semibold focus:outline-none ${form.visitType === "walkin" ? "opacity-50 cursor-not-allowed" : ""}`}
            style={{ color: "var(--text-primary)", colorScheme: isDark ? "dark" : "light" }}
              value={appointmentDate}
              onChange={handleDateChange}
            />
          </div>
        </div>

        {/* ============================
    DEPARTMENT (FULL WIDTH MATCH)
=============================== */}
        <div className="mt-5 w-full md:w-1/2">
          <div className="flex items-center justify-between w-full mb-2">
            <label className="text-xs font-bold uppercase tracking-widest text-gray-400">
              {t("doctor.department")}
            </label>
            {/* All Departments toggle */}
            <label className="flex items-center gap-2 cursor-pointer select-none group">
              <input
                type="checkbox"
                className="hidden"
                checked={anyDepartment}
                onChange={async (e) => {
                  const checked = e.target.checked;
                  setAnyDepartment(checked);
                  const visitType =
                    form.visitType === "appointment"
                      ? VISIT_TYPE.APPOINTMENT
                      : VISIT_TYPE.WALK_IN;



                  if (checked) {
                    onChange({ target: { name: "selectedDepartment", value: "" } });
                    onChange({ target: { name: "selectedDoctor", value: "" } });
                    setDoctors([]);
                    setDoctorFee(null);

                    try {
                      setDoctorError(null);
                      setLoadingMessage("Fetching Doctors...");
                      setLoading(true);
                      const res = await getDoctors(visitType, null, form.appointmentDate);
                      setDoctors(res?.data || []);
                    } catch (err) {
                      setDoctors([]);
                      setDoctorError("Failed to load doctors");
                    }
                    finally {
                      setLoading(false);
                    }
                  } else {
                    onChange({ target: { name: "selectedDoctor", value: "" } });
                    setDoctors([]);
                    setDoctorFee(null);

                    if (form.selectedDepartment) {
                      try {
                        setDoctorError(null);
                        setLoadingMessage("Fetching Doctors...");
                        setLoading(true);
                        const res = await getDoctors(visitType, form.selectedDepartment, form.appointmentDate);
                        setDoctors(res?.data || []);
                      } catch (err) {
                        setDoctors([]);
                        setDoctorError("Failed to load doctors");
                      }
                      finally {
                        setLoading(false);
                      }
                    }
                  }
                }}
              />

              {/* Toggle switch track */}
              <div
                className="relative flex-shrink-0 transition-all duration-300"
                style={{
                  width: 36, height: 20,
                  borderRadius: 10,
                  background: anyDepartment
                    ? "linear-gradient(135deg,#2563eb,#0891b2)"
                    : isDark ? "#334155" : "#d1d5db",
                  boxShadow: anyDepartment ? "0 2px 8px rgba(37,99,235,0.35)" : "none",
                }}
              >
                <div
                  className="absolute top-0.5 transition-all duration-300 rounded-full bg-white shadow"
                  style={{
                    width: 16, height: 16,
                    left: anyDepartment ? 18 : 2,
                  }}
                />
              </div>

              {/* Label + icon */}
              <div className="flex items-center gap-1">
                <Layers
                  size={13}
                  style={{ color: anyDepartment ? (isDark ? "#60a5fa" : "#2563eb") : (isDark ? "#64748b" : "#9ca3af") }}
                />
                <span
                  className="text-xs font-bold transition-all"
                  style={{ color: anyDepartment ? (isDark ? "#60a5fa" : "#2563eb") : (isDark ? "#64748b" : "#9ca3af") }}
                >
                  All Depts
                </span>
              </div>
            </label>
          </div>



          <Select styles={reactSelectStyles(isDark, anyDepartment)}
            menuPortalTarget={document.body}
            classNamePrefix="react-select"
            isDisabled={anyDepartment}
            options={departments.map(dept => ({
              value: dept.departmentCode,
              label: dept.departmentName
            }))}
            value={
              form.selectedDepartment
                ? { value: form.selectedDepartment, label: departments.find(d => d.departmentCode === form.selectedDepartment)?.departmentName }
                : null
            }
            onChange={(selected) => {
              onChange({ target: { name: "selectedDepartment", value: selected?.value || "" } });
              // fetch doctors normally if anyDepartment is false
              if (!anyDepartment && selected?.value) {
                try {
                  const visitType =
                    form.visitType === "appointment"
                      ? VISIT_TYPE.APPOINTMENT
                      : VISIT_TYPE.WALK_IN;
                  setLoadingMessage("Fetching Doctors...");
                  setLoading(true);
                  const res = getDoctors(visitType, selected.value, form.appointmentDate).then(res => setDoctors(res?.data || []));
                  setDoctors(res?.data || []);
                } catch (err) {
                  console.error("Error fetching doctors:", err.response || err);
                  setDoctorError("Failed to load doctors");
                  setDoctors([]);
                } finally {
                  setLoading(false);
                }
              }
            }}
            placeholder={loadingDepartments ? "Loading..." : deptError ? deptError : t("doctor.selectDepartment")}
            isClearable
          />
          <p className="text-[11px] mt-1.5 italic flex items-center gap-1"
             style={{ color: anyDepartment ? (isDark ? "#60a5fa" : "#2563eb") : (isDark ? "#64748b" : "#9ca3af") }}>
            {anyDepartment
              ? <><Layers size={11} /> Showing all doctors across departments</>
              : t("bookings.hintDepartment")}
          </p>
        </div>

        {/* DOCTOR SELECTION */}
        <div className="w-full mt-5">
          <label className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2 block">
            {t("doctor.doctor")} <span className="text-red-500">*</span>
          </label>

          {/* Doctor dropdown */}
          <div className="w-full md:w-1/2">
            <Select styles={reactSelectStyles(isDark)}
              classNamePrefix="react-select"
              options={doctors.map(doc => ({
                value: doc.doctorId,
                label: doctorFee && form.selectedDoctor === doc.doctorId ? `${doc.name} (₹${doctorFee})` : doc.name
              }))}
              value={
                form.selectedDoctor
                  ? { value: form.selectedDoctor, label: doctors.find(d => d.doctorId === form.selectedDoctor)?.name }
                  : null
              }
              onChange={(selected) => {
                handleDoctorChange({ target: { name: "selectedDoctor", value: selected?.value || "" } });
              }}
              required
              placeholder={t("doctor.selectDoctor")}
              isClearable
            />
            <p className="text-[11px] text-gray-400 mt-1.5 italic">{t("bookings.hintSlot")}</p>

            {/* Fee + slot chips appear after doctor selected */}
            {form.selectedDoctor && (
              <div className="flex flex-wrap gap-2 mt-2">
                {doctorFee !== null && doctorFee !== undefined && (
                  <span
                    className="inline-flex items-center gap-1 text-[11px] font-extrabold px-2.5 py-1 rounded-xl"
                    style={doctorFee === 0
                      ? { background: isDark ? "#052e16" : "#d1fae5", color: isDark ? "#6ee7b7" : "#065f46", border: `1px solid ${isDark ? "#166534" : "#6ee7b7"}` }
                      : { background: "var(--badge-blue-bg)", color: "var(--badge-blue-text)", border: "1px solid var(--badge-blue-border)" }}
                  >
                    {doctorFee === 0 ? "✓ Free Consultation" : `₹${doctorFee} Consultation Fee`}
                  </span>
                )}
                {form.visitType === "walkin" && isWalkinsAllowed && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-xl"
                        style={{ background: isDark ? "#052e16" : "#f0fdf4", color: isDark ? "#86efac" : "#15803d", border: `1px solid ${isDark ? "#166534" : "#bbf7d0"}` }}>
                    ✓ Walk-ins available
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Fee & Slot horizontal container */}
          {/* <div className="flex items-center gap-3 mt-3">

             {doctorFee !== null && doctorFee !== undefined && (
              <div className="px-3 py-1.5 bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-200 rounded-md text-xs font-medium flex flex-col items-center">
                <span className="font-semibold text-sm">₹{doctorFee}</span>
                <span className="text-[10px] opacity-80 mt-0.5 text-center w-full">Consultation Fee</span>
              </div>
            )}

           
            {form.slotDateTime && (
              <div className="px-3 py-1.5 bg-yellow-100 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200 rounded-md text-xs font-medium flex flex-col items-center">

                 <span className="text-sm">
                  {form.slotDateTime.startTime} - {form.slotDateTime.endTime}
                </span>

                 {form.slotDateTime.period && (
                  <span className="text-[9px] opacity-90 mt-0.5 text-center flex flex-col items-center w-full">
                     <span className="text-[10px]">{form.slotDateTime.period === "morning" && "🌅"}
                      {form.slotDateTime.period === "afternoon" && "☀️"}
                      {form.slotDateTime.period === "evening" && "🌙"}{form.slotDateTime.period}</span>

                  </span>
                )}
              </div>
            )} 
          </div> */}

        </div>

        {/* ============================
    SPECIALITY SELECTOR
=============================== */}
        {(() => {
          const selectedDoc = doctors.find(d => d.doctorId === form.selectedDoctor);
          const specialities = selectedDoc?.specialities;
          if (!form.selectedDoctor || !specialities?.length) return null;

          const options = specialities.map(s =>
            typeof s === "string"
              ? { value: s, label: s }
              : { value: s.id ?? s.code ?? s.name, label: s.name }
          );

          return (
            <div className="w-full md:w-1/2 mt-4">
              <label className="label text-gray-700 dark:text-gray-200">
                {t("doctor.speciality")} <span className="text-red-500">*</span>
              </label>
              <Select
                styles={reactSelectStyles(isDark)}
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                options={options}
                value={selectedSpeciality}
                onChange={(selected) => {
                  setSelectedSpeciality(selected);
                  onChange({ target: { name: "selectedSpeciality", value: selected?.value || null } });
                }}
                placeholder={t("doctor.selectSpeciality")}
                isClearable
              />
            </div>
          );
        })()}

        {/* ============================
    PAYMENT MODE
=============================== */}
        {(showPayNow || showPayCounter) && (
          <div className="mt-5 w-full md:w-1/2">
            <label className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2 block">
              {t("payment.choosePaymentMode")}
            </label>
            <div className="grid grid-cols-1 gap-3">
              {showPayNow && (
                <div
                  onClick={() => !forcePayNow && !isFreeConsultation && handleChange("pay_now")}
                  className="flex flex-col items-start gap-1 p-3.5 rounded-2xl transition-all duration-200 active:scale-95"
                  style={{
                    cursor: forcePayNow || isFreeConsultation ? "not-allowed" : "pointer",
                    opacity: isFreeConsultation ? 0.5 : 1,
                    background: form.paymentMode === "pay_now"
                      ? (isDark ? "#1e3a5f" : "#eff6ff")
                      : "var(--surface-secondary)",
                    border: "1.5px solid " + (form.paymentMode === "pay_now" ? "#2563eb" : "var(--border)"),
                    boxShadow: form.paymentMode === "pay_now" ? "0 2px 12px rgba(37,99,235,0.15)" : "none",
                  }}
                >
                  <div className="flex items-start justify-between w-full gap-2">
                    <span className="font-bold text-sm min-w-0" style={{ color: "var(--text-primary)", overflowWrap: "break-word", wordBreak: "break-word" }}>💳 {t("payment.payNow")}</span>
                    {form.paymentMode === "pay_now" && (
                      <span className="w-5 h-5 flex-shrink-0 rounded-full flex items-center justify-center text-[10px] font-extrabold text-white"
                            style={{ background: "#2563eb" }}>✓</span>
                    )}
                  </div>
                  <p className="text-[11px] text-gray-400" style={{ overflowWrap: "break-word" }}>{t("doctor.instantOnline")}</p>
                </div>
              )}
              {showPayCounter && (
                <div
                  onClick={() => !disablePayCounter && !isFreeConsultation && handleChange("pay_counter")}
                  className="flex flex-col items-start gap-1 p-3.5 rounded-2xl transition-all duration-200 active:scale-95"
                  style={{
                    cursor: disablePayCounter || isFreeConsultation ? "not-allowed" : "pointer",
                    opacity: disablePayCounter || isFreeConsultation ? 0.5 : 1,
                    background: form.paymentMode === "pay_counter"
                      ? (isDark ? "#052e16" : "#f0fdf4")
                      : "var(--surface-secondary)",
                    border: "1.5px solid " + (form.paymentMode === "pay_counter" ? "#10b981" : "var(--border)"),
                    boxShadow: form.paymentMode === "pay_counter" ? "0 2px 12px rgba(16,185,129,0.15)" : "none",
                  }}
                >
                  <div className="flex items-start justify-between w-full gap-2">
                    <span className="font-bold text-sm min-w-0" style={{ color: "var(--text-primary)", overflowWrap: "break-word", wordBreak: "break-word" }}>🏥 {t("payment.payAtCounter")}</span>
                    {form.paymentMode === "pay_counter" && (
                      <span className="w-5 h-5 flex-shrink-0 rounded-full flex items-center justify-center text-[10px] font-extrabold text-white"
                            style={{ background: "#10b981" }}>✓</span>
                    )}
                  </div>
                  <p className="text-[11px] text-gray-400" style={{ overflowWrap: "break-word" }}>{t("doctor.payAtReception")}</p>
                </div>
              )}
            </div>
          </div>
        )}


        <div
          role="button"
          tabIndex={0}
          onClick={() => setShowNotes(v => !v)}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setShowNotes(v => !v)}
          className="
            sm:hidden
            mt-2
            inline-flex items-center gap-2
            px-3 py-1.5
            rounded-full
            text-sm font-medium
            text-gray-600 dark:text-gray-300
            bg-gray-100 dark:bg-gray-700
            hover:bg-gray-200 dark:hover:bg-gray-600
            cursor-pointer
            transition
            select-none
          "
          aria-pressed={showNotes}
          aria-label={showNotes ? t("notes.hide") : t("notes.add")}
        >
          <span className="text-base">📝</span>
          <span>{showNotes ? t("notes.hide") : t("notes.add")}</span>
        </div>


        {/* Notes */}
        <div className={`mt-4 ${showNotes ? "block" : "hidden"} sm:block`}>
          <label className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2 block">{t("doctor.notes")}</label>
          <textarea
            rows="3"
            name="specialNotes"
            value={form.specialNotes}
            onChange={onChange}
            className="input resize-none w-full rounded-xl px-3 py-2.5 text-sm text-gray-800 transition-all focus:outline-none"
            style={{
              background: "var(--surface-secondary)",
              border: "1.5px solid var(--border)",
              color: "var(--text-primary)",
              boxShadow: "none",
            }}
            onFocus={e => { e.target.style.borderColor = isDark ? "#3b82f6" : "#bfdbfe"; e.target.style.boxShadow = "0 0 0 3px rgba(37,99,235,0.08)"; }}
            onBlur={e => { e.target.style.borderColor = "var(--border)"; e.target.style.boxShadow = "none"; }}
            placeholder={t("doctor.notesPlaceholder")}
          ></textarea>



        </div>
        <AvailableSlotsPopup
          open={openSlots}
          onClose={setOpenSlots}
          slots={slots} appointmentDate={appointmentDate}
          doctorId={selectedDoctorId}
          onSelectSlot={handleSlotSelect}
        />

        {form.selectedDoctor && ReactDOM.createPortal(
          <>
            {/* Bottom sheet */}
            <div
              className="fixed bottom-0 left-0 right-0 z-30 animate-[slideUp_0.3s_ease-out]"
              style={{
                background: isDark ? "rgba(10,18,40,0.96)" : "rgba(255,255,255,0.97)",
                backdropFilter: "blur(32px)",
                WebkitBackdropFilter: "blur(32px)",
                borderTop: "1px solid " + (isDark ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.9)"),
                boxShadow: isDark
                  ? "0 -24px 60px rgba(0,0,0,0.6), 0 -8px 20px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)"
                  : "0 -24px 60px rgba(15,40,120,0.2), 0 -8px 20px rgba(15,40,120,0.1), inset 0 1px 0 rgba(255,255,255,1)",
                borderRadius: "20px 20px 0 0",
                paddingBottom: "calc(env(safe-area-inset-bottom) + 12px)",
              }}
            >
              {/* Drag handle */}
              <div className="flex justify-center pt-3 pb-2">
                <div className="w-12 h-1.5 rounded-full"
                     style={{ background: isDark ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.18)" }} />
              </div>

              {/* Sheet title */}
              <div className="px-5 pb-3 flex items-start justify-between">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-widest mb-1"
                     style={{ color: isDark ? "#60a5fa" : "#2563eb" }}>
                    {t("tabs.bookingSummary")}
                  </p>
                  <p className="font-extrabold text-base" style={{ color: "var(--text-primary)", letterSpacing: "-0.02em" }}>
                    {t("panel.dr")} {form.selectedDoctorName || "—"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleDoctorChange({ target: { name: "selectedDoctor", value: "" } })}
                  className="flex-shrink-0 flex items-center justify-center rounded-full transition-all duration-150 active:scale-90"
                  style={{
                    width: 28, height: 28,
                    background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
                    color: isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.4)",
                    border: "none",
                    cursor: "pointer",
                    marginTop: 2,
                  }}
                  title="Close"
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <line x1="1" y1="1" x2="11" y2="11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <line x1="11" y1="1" x2="1" y2="11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>

              {/* Divider */}
              <div className="mx-5"
                   style={{ height: 1, background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.07)" }} />

              {/* Content */}
              <div className="px-5 py-4 space-y-4">

                {/* Slot conflict error */}
                {slotConflictError && (
                  <div className="flex items-start justify-between gap-3 px-3 py-2.5 rounded-2xl"
                       style={{ background: isDark ? "#450a0a" : "#fef2f2", border: `1px solid ${isDark ? "#7f1d1d" : "#fecaca"}`, color: isDark ? "#fca5a5" : "#dc2626" }}>
                    <div className="flex gap-2 items-start text-[12px]">
                      <span className="text-base">⚠</span>
                      <span>{slotConflictError}</span>
                    </div>
                    <button type="button" onClick={() => setSlotConflictError(null)}
                            className="opacity-60 hover:opacity-100 leading-none">✕</button>
                  </div>
                )}

                {/* Info rows */}
                <div className="space-y-2.5">
                  {form.appointmentDate && (
                    <div className="flex items-center justify-between">
                      <span className="text-[12px] font-semibold" style={{ color: "var(--text-muted)" }}>{t("panel.date")}</span>
                      <span className="text-[12px] font-bold px-2.5 py-1 rounded-full"
                            style={{ background: isDark ? "#1e3a5f" : "#eff6ff", color: isDark ? "#93c5fd" : "#1d4ed8" }}>
                        📅 {new Date(form.appointmentDate).toLocaleDateString("en-GB")}
                      </span>
                    </div>
                  )}
                  {form.slotDateTime && (
                    <div className="flex items-center justify-between">
                      <span className="text-[12px] font-semibold" style={{ color: "var(--text-muted)" }}>{t("panel.slot")}</span>
                      <span className="text-[12px] font-bold px-2.5 py-1 rounded-full"
                            style={{ background: isDark ? "#451a03" : "#fffbeb", color: isDark ? "#fcd34d" : "#92400e" }}>
                        ⏰ {form.slotDateTime.startTime}–{form.slotDateTime.endTime}
                      </span>
                    </div>
                  )}
                  {doctorFee !== null && doctorFee !== undefined && (
                    <div className="flex items-center justify-between">
                      <span className="text-[12px] font-semibold" style={{ color: "var(--text-muted)" }}>{t("panel.fee")}</span>
                      <span className="text-[12px] font-bold px-2.5 py-1 rounded-full"
                            style={isFreeConsultation
                              ? { background: isDark ? "#052e16" : "#d1fae5", color: isDark ? "#6ee7b7" : "#065f46" }
                              : { background: isDark ? "#14532d" : "#f0fdf4", color: isDark ? "#86efac" : "#15803d" }}>
                        {isFreeConsultation ? t("panel.freeConsultation") : `₹${doctorFee}`}
                      </span>
                    </div>
                  )}
                  {form.paymentMode && !isFreeConsultation && (
                    <div className="flex items-center justify-between">
                      <span className="text-[12px] font-semibold" style={{ color: "var(--text-muted)" }}>{t("panel.payment")}</span>
                      <span className="text-[12px] font-bold px-2.5 py-1 rounded-full"
                            style={form.paymentMode === "pay_counter"
                              ? { background: isDark ? "#451a03" : "#fef3c7", color: isDark ? "#fbbf24" : "#92400e" }
                              : { background: isDark ? "#1e3a5f" : "#eff6ff", color: isDark ? "#93c5fd" : "#1d4ed8" }}>
                        {form.paymentMode === "pay_counter" ? t("panel.atCounter") : t("panel.online")}
                      </span>
                    </div>
                  )}
                </div>

                {/* Divider */}
                <div style={{ height: 1, background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)" }} />

                {/* Consultation type */}
                {form.visitType && (
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-widest mb-2.5"
                       style={{ color: "var(--text-muted)" }}>
                      {t("panel.consultationType")}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => onChange({ target: { name: "appointmentType", value: "in_person" } })}
                        className="flex items-center gap-2 px-4 py-2 rounded-2xl text-xs font-bold transition-all active:scale-95"
                        style={form.appointmentType !== "telemedicine"
                          ? { background: "linear-gradient(135deg,#2563eb,#0891b2)", color: "#fff", boxShadow: "0 3px 10px rgba(37,99,235,0.35)", touchAction: "manipulation" }
                          : { background: isDark ? "#1e293b" : "#f3f4f6", color: isDark ? "#94a3b8" : "#6b7280", border: `1px solid ${isDark ? "#334155" : "#e5e7eb"}`, touchAction: "manipulation" }}
                      >
                        <FaStethoscope />
                        {t("consultation.inPerson")}
                      </button>
                      {form.visitType === "appointment" && showTelemedicine && (
                        <button
                          type="button"
                          onClick={() => onChange({ target: { name: "appointmentType", value: "telemedicine" } })}
                          className="flex items-center gap-2 px-4 py-2 rounded-2xl text-xs font-bold transition-all active:scale-95"
                          style={form.appointmentType === "telemedicine"
                            ? { background: "linear-gradient(135deg,#7c3aed,#6d28d9)", color: "#fff", boxShadow: "0 3px 10px rgba(124,58,237,0.35)", touchAction: "manipulation" }
                            : { background: isDark ? "#1e293b" : "#f3f4f6", color: isDark ? "#94a3b8" : "#6b7280", border: `1px solid ${isDark ? "#334155" : "#e5e7eb"}`, touchAction: "manipulation" }}
                        >
                          <FaVideo />
                          {t("consultation.telemedicine")}
                        </button>
                      )}
                    </div>
                    {form.visitType === "appointment" && form.appointmentType === "telemedicine" && (
                      <div className="mt-3 px-3 py-2.5 rounded-2xl text-[11px] flex flex-col gap-1.5"
                           style={{ background: isDark ? "rgba(88,28,135,0.25)" : "#faf5ff", border: `1px solid ${isDark ? "#4c1d95" : "#e9d5ff"}`, color: isDark ? "#c4b5fd" : "#7e22ce" }}>
                        <div className="flex gap-2 items-start">
                          <FaVideo className="flex-shrink-0 mt-0.5" />
                          {t("consultation.telemedicineInfo")}
                        </div>
                        <div className="flex items-center gap-1.5 cursor-pointer mt-1" onClick={() => setShowTeleInfo(true)}>
                          <FaInfoCircle className="flex-shrink-0" />
                          <span className="text-[10px] font-semibold underline underline-offset-2">{t("consultation.moreInfo")}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Fixed book button footer */}
              <div className="px-5 pt-3 pb-4 flex-shrink-0 space-y-2.5"
                   style={{ borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)"}` }}>
                <button
                  type="submit"
                  form="patient-form"
                  className="w-full font-extrabold text-white rounded-2xl active:scale-95 transition-all duration-150"
                  style={{
                    background: "linear-gradient(135deg,#2563eb,#0891b2)",
                    boxShadow: "0 6px 20px rgba(37,99,235,0.45)",
                    touchAction: "manipulation",
                    minHeight: 54,
                    fontSize: "1rem",
                    letterSpacing: "-0.01em",
                    cursor: "pointer",
                  }}
                >
                  {isFreeConsultation ? t("panel.bookFree") : t("panel.bookNow", { fee: doctorFee ?? 0 })}
                </button>
                {form.paymentMode === "pay_now" && (
                  <div className="text-center">
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2.5 py-0.5 rounded-full"
                          style={{ background: "rgba(16,185,129,0.1)", color: "#059669", border: "1px solid rgba(16,185,129,0.2)" }}>
                      {t("panel.securePayment")}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </>, document.body
        )}


      </div>
      <TelemedicineInfoPopup
        show={showTeleInfo}
        onClose={() => setShowTeleInfo(false)}
        title={t("consultation.telemedicineInfoTitle")}
        content={t("consultation.telemedicineInfoDetails")}
      />

    </div>
  );
}
